import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setSearchQuery } from '../../redux/slices/uiSlice';
import { motion, AnimatePresence } from 'framer-motion';
import {
    collection,
    query,
    getDocs,
    where,
    orderBy,
    startAt,
    endAt,
    limit,
    doc,
    setDoc,
    getDoc,
    serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { IconSearch, IconUserCircle, IconBriefcase, IconHistory } from '@tabler/icons-react';
import { fetchUsersByIds, selectUserById } from '../../redux/slices/usersCacheSlice';
import { getDisplayName, getResolvedPhotoURL } from '../../utils/userDisplay.js';

const SearchComponent = () => {
    const dispatch = useDispatch();
    const searchQuery = useSelector(state => state.ui.searchQuery);
    const [liveResults, setLiveResults] = useState([]); // users
    const [livePostResults, setLivePostResults] = useState([]);
    const [liveSolutionResults, setLiveSolutionResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const searchRef = useRef(null);
    const navigate = useNavigate();
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [userNavigated, setUserNavigated] = useState(false);
    const listboxId = 'search-dropdown-listbox';
    const authUid = useSelector(state => state.auth?.currentUser?.uid);
    const userIdFromData = useSelector(state => state.auth?.userData?.id);
    const storageUserKey = authUid || userIdFromData || 'anon';
    const [recentSearches, setRecentSearches] = useState([]);
    const RECENT_CAP = 8;

    const STORAGE_KEY = useMemo(() => `recent_searches:${storageUserKey}`, [storageUserKey]);

    // Load recent searches on user change: prefer Firestore if authenticated, else localStorage
    useEffect(() => {
        const loadRecents = async () => {
            try {
                // First read localStorage as a fallback
                let local = [];
                try {
                    const raw = localStorage.getItem(STORAGE_KEY);
                    if (raw) {
                        const parsed = JSON.parse(raw);
                        local = Array.isArray(parsed) ? parsed.slice(0, RECENT_CAP) : [];
                    }
                } catch { }

                // If authenticated, try Firestore
                if (authUid) {
                    try {
                        const ref = doc(db, 'users', authUid, 'meta', 'search');
                        const snap = await getDoc(ref);
                        if (snap.exists()) {
                            const server = Array.isArray(snap.data()?.recent) ? snap.data().recent.slice(0, RECENT_CAP) : [];
                            setRecentSearches(server);
                            // Keep localStorage in sync
                            try { localStorage.setItem(STORAGE_KEY, JSON.stringify(server)); } catch { }
                            return;
                        }
                    } catch (e) {
                        console.warn('Recent searches Firestore fetch failed, using local fallback:', e?.message || e);
                    }
                }
                // Fallback to local
                setRecentSearches(local);
            } catch {
                setRecentSearches([]);
            }
        };
        loadRecents();
    }, [STORAGE_KEY, authUid]);

    const addRecentTerm = async (term) => {
        const t = (term || '').trim();
        if (t.length < 2 || t.length > 64) return;
        const next = [t, ...recentSearches.filter(s => s.toLowerCase() !== t.toLowerCase())].slice(0, RECENT_CAP);
        setRecentSearches(next);
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { }
        // Persist to Firestore for authenticated users
        if (authUid) {
            try {
                const ref = doc(db, 'users', authUid, 'meta', 'search');
                await setDoc(ref, { recent: next, updatedAt: serverTimestamp() }, { merge: true });
            } catch (e) {
                console.warn('Failed to persist recent search to Firestore:', e?.message || e);
            }
        }
    };

    const clearRecent = async () => {
        setRecentSearches([]);
        try { localStorage.removeItem(STORAGE_KEY); } catch { }
        if (authUid) {
            try {
                const ref = doc(db, 'users', authUid, 'meta', 'search');
                await setDoc(ref, { recent: [], updatedAt: serverTimestamp() }, { merge: true });
            } catch (e) {
                console.warn('Failed to clear recent searches in Firestore:', e?.message || e);
            }
        }
    };

    // Flattened selectable options for keyboard nav
    const options = useMemo(() => {
        const trimmed = searchQuery.trim();
        if (trimmed.length >= 3) {
            const users = liveResults.map((u) => ({ type: 'user', value: u }));
            const posts = livePostResults.map((p) => ({ type: 'post', value: p }));
            const solutions = liveSolutionResults.map((s) => ({ type: 'solution', value: s }));
            const merged = [...users, ...posts, ...solutions];
            if (merged.length === 0) {
                return recentSearches.map((t) => ({ type: 'recent', value: t }));
            }
            return merged;
        }
        return recentSearches.map((t) => ({ type: 'recent', value: t }));
    }, [liveResults, livePostResults, liveSolutionResults, searchQuery, recentSearches]);

    // Tokenizer for posts/solutions title words
    const getTokens = (s) => {
        if (!s) return [];
        const tokens = String(s).toLowerCase().split(/[^a-z0-9]+/gi).filter(Boolean);
        const uniq = Array.from(new Set(tokens));
        return uniq.slice(0, 10);
    };

    // Highlight helper for matched substrings (safe split-with-capture)
    const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const renderHighlighted = (text) => {
        const trimmed = searchQuery.trim();
        if (!text || trimmed.length < 2) return String(text || '');
        const tokens = getTokens(trimmed);
        if (!tokens.length) return String(text || '');
        const pattern = new RegExp(`(${tokens.map(escapeRegExp).join('|')})`, 'gi');
        const parts = String(text).split(pattern);
        return parts.map((part, idx) => (
            idx % 2 === 1 ? <mark key={idx} className="hl-mark">{part}</mark> : <span key={idx}>{part}</span>
        ));
    };

    // Handle live search with debounce across users + posts + solutions, with cancellation guards
    useEffect(() => {
        let isActive = true;
        const timer = setTimeout(async () => {
            const trimmed = searchQuery.trim();
            if (trimmed.length >= 3) {
                setLoading(true);
                const searchTerm = trimmed.toLowerCase();
                const tokens = getTokens(trimmed);

                try {
                    const usersRef = collection(db, 'users');
                    let users = [];

                    // Strategy 1: Search by username_lower (works with existing index)
                    try {
                        const uq = query(
                            usersRef,
                            orderBy('username_lower'),
                            startAt(searchTerm),
                            endAt(searchTerm + '\uf8ff'),
                            limit(5)
                        );
                        const us = await getDocs(uq);
                        users = us.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    } catch (error) {
                        console.log('username_lower search failed:', error.message);
                    }

                    // Strategy 2: Fallback - Get ALL users and filter client-side (for missing username_lower)
                    if (users.length === 0) {
                        try {
                            // Fetch more users for better fallback coverage
                            const fallbackQuery = query(
                                usersRef,
                                orderBy('createdAt', 'desc'),
                                limit(50)
                            );
                            const fallbackSnapshot = await getDocs(fallbackQuery);
                            const allUsers = fallbackSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                            // Filter client-side by multiple fields
                            const filteredUsers = allUsers.filter(user => {
                                const searchableFields = [
                                    user.username?.toLowerCase(),
                                    user.displayName?.toLowerCase(),
                                    user.name?.toLowerCase(),
                                    user.username_lower?.toLowerCase(),
                                ].filter(Boolean);

                                return searchableFields.some(field =>
                                    field.includes(searchTerm)
                                );
                            });

                            users = filteredUsers.slice(0, 5);
                            if (users.length > 0) {
                                console.log('[Search] Fallback search found', users.length, 'users');
                            }
                        } catch (error) {
                            console.log('Fallback search failed:', error.message);
                        }
                    }

                    // Posts (any token)
                    const postsRef = collection(db, 'posts');
                    const postConstraints = [];
                    if (tokens.length) {
                        postConstraints.push(where('title_words_lower', 'array-contains-any', tokens));
                        postConstraints.push(where('status', '==', 'active'));
                        postConstraints.push(orderBy('timestamp', 'desc'));
                    }
                    postConstraints.push(limit(2));
                    const pq = query(postsRef, ...postConstraints);

                    // Solutions (any token)
                    const solutionsRef = collection(db, 'solutions');
                    const solConstraints = [];
                    if (tokens.length) {
                        solConstraints.push(where('title_words_lower', 'array-contains-any', tokens));
                        solConstraints.push(orderBy('updatedAt', 'desc'));
                    }
                    solConstraints.push(limit(2));
                    const sq = query(solutionsRef, ...solConstraints);

                    const [ps, ss] = await Promise.all([
                        tokens.length ? getDocs(pq) : Promise.resolve({ docs: [] }),
                        tokens.length ? getDocs(sq) : Promise.resolve({ docs: [] })
                    ]);
                    if (!isActive) return;
                    const posts = ps.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    const solutions = ss.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                    setLiveResults(users);
                    setLivePostResults(posts);
                    setLiveSolutionResults(solutions);

                    const ids = users.map(r => r.id).filter(Boolean);
                    if (ids.length) {
                        // Throttle user fetching to prevent excessive dispatches
                        setTimeout(() => {
                            dispatch(fetchUsersByIds({ userIds: ids }));
                        }, 100);
                    }

                    setShowDropdown(true);
                } catch (error) {
                    console.error('Error fetching live results:', error);
                } finally {
                    if (isActive) setLoading(false);
                }
            } else {
                setLiveResults([]);
                setLivePostResults([]);
                setLiveSolutionResults([]);
                setShowDropdown(searchQuery.length > 0);
            }
        }, 300);

        return () => {
            isActive = false;
            clearTimeout(timer);
        };
    }, [searchQuery, dispatch]);

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Maintain highlighted option when dropdown visibility or options change
    useEffect(() => {
        if (!showDropdown) {
            setHighlightedIndex(-1);
            setUserNavigated(false);
        } else {
            setHighlightedIndex(options.length ? 0 : -1);
        }
    }, [showDropdown, options]);

    const handleSearch = async (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            addRecentTerm(searchQuery);
            navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
            dispatch(setSearchQuery(''));
            setShowDropdown(false);
        }
    };

    const handleInputChange = (e) => {
        dispatch(setSearchQuery(e.target.value));
        setUserNavigated(false);
    };

    const handleCardClick = (userId) => {
        if (authUid && userId === authUid) {
            navigate('/profile');
        } else {
            navigate(`/profile/${userId}`);
        }
        setShowDropdown(false);
        dispatch(setSearchQuery(''));
    };

    const selectOptionAtIndex = (idx) => {
        const opt = options[idx];
        if (!opt) return;
        if (opt.type === 'user') {
            handleCardClick(opt.value.id);
        } else if (opt.type === 'post') {
            const term = searchQuery.trim();
            if (!term) return;
            addRecentTerm(term);
            navigate(`/search?q=${encodeURIComponent(term)}&tab=posts`);
            setShowDropdown(false);
            dispatch(setSearchQuery(''));
        } else if (opt.type === 'solution') {
            const term = searchQuery.trim();
            if (!term) return;
            addRecentTerm(term);
            navigate(`/search?q=${encodeURIComponent(term)}&tab=solutions`);
            setShowDropdown(false);
            dispatch(setSearchQuery(''));
        } else if (opt.type === 'recent') {
            const term = (opt.value || '').trim();
            if (!term) return;
            addRecentTerm(term);
            navigate(`/search?q=${encodeURIComponent(term)}`);
            setShowDropdown(false);
            dispatch(setSearchQuery(''));
        }
    };

    const handleKeyDown = (e) => {
        if (!showDropdown) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const next = (highlightedIndex + 1) % (options.length || 1);
            setHighlightedIndex(options.length ? next : -1);
            setUserNavigated(true);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const next = (highlightedIndex - 1 + (options.length || 1)) % (options.length || 1);
            setHighlightedIndex(options.length ? next : -1);
            setUserNavigated(true);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (userNavigated && highlightedIndex >= 0 && options.length) {
                selectOptionAtIndex(highlightedIndex);
            } else {
                // Submit typed query instead of defaulting to the first option
                handleSearch(e);
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setShowDropdown(false);
            setHighlightedIndex(-1);
            setUserNavigated(false);
        }
    };

    return (
        <div className="relative w-[342px] z-10" ref={searchRef}>
            <form onSubmit={handleSearch} className="w-full">
                <div className="relative w-full">
                    <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-4 h-4 pointer-events-none" size={20} />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={handleInputChange}
                        onFocus={() => setShowDropdown(true)}
                        onKeyDown={handleKeyDown}
                        role="combobox"
                        aria-expanded={showDropdown}
                        aria-controls={listboxId}
                        aria-autocomplete="list"
                        aria-activedescendant={highlightedIndex >= 0 ? `${listboxId}-option-${highlightedIndex}` : undefined}
                        placeholder="Search users, posts, solutions..."
                        className="w-full h-10 py-2 px-9 border border-gray-200 rounded-3xl text-base text-gray-900 transition-all bg-white focus:border-[#31C65B] focus:outline-none focus:ring-2 focus:ring-[#31C65B] focus:ring-opacity-20"
                    />
                </div>
            </form>

            <AnimatePresence>
                {showDropdown && (
                    <motion.div
                        className="absolute top-[calc(100%+8px)] left-0 w-4/5 bg-white rounded-xl shadow-lg p-3 max-h-[150px] overflow-y-auto z-[1500] md:w-full"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                    >
                        {loading ? (
                            <div className="flex items-center justify-center p-5 text-gray-600">
                                <div className="w-4 h-4 border-2 border-[#31C65B] border-t-transparent rounded-full animate-spin mr-2" />
                                <p>Searching...</p>
                            </div>
                        ) : (
                            <>
                                {searchQuery.trim().length >= 2 ? (
                                    <div className="flex flex-col gap-2" role="listbox" id={listboxId}>
                                        {/* Users section */}
                                        {liveResults.length > 0 && (
                                            <>
                                                <h3 className="text-xs uppercase text-gray-400 px-1" aria-hidden="true">Users</h3>
                                                {liveResults.map((user) => {
                                                    const idx = options.findIndex(o => o.type === 'user' && o.value.id === user.id);
                                                    return (
                                                        <motion.div
                                                            key={user.id}
                                                            id={`${listboxId}-option-${idx}`}
                                                            role="option"
                                                            aria-selected={highlightedIndex === idx}
                                                            className={`flex items-center p-3 rounded-lg transition-all cursor-pointer ${highlightedIndex === idx ? 'bg-gray-100' : 'bg-gray-50 hover:bg-gray-100'}`}
                                                            whileHover={{ scale: 1.02 }}
                                                            onMouseEnter={() => { setHighlightedIndex(idx); setUserNavigated(true); }}
                                                            onClick={() => selectOptionAtIndex(idx)}
                                                        >
                                                            <LiveResultItem user={user} query={searchQuery} renderHighlighted={renderHighlighted} />
                                                        </motion.div>
                                                    );
                                                })}
                                            </>
                                        )}

                                        {/* Posts section */}
                                        {livePostResults.length > 0 && (
                                            <>
                                                <h3 className="text-xs uppercase text-gray-400 px-1 mt-2" aria-hidden="true">Posts</h3>
                                                {livePostResults.map((post) => {
                                                    const idx = options.findIndex(o => o.type === 'post' && o.value.id === post.id);
                                                    return (
                                                        <div
                                                            key={post.id}
                                                            id={`${listboxId}-option-${idx}`}
                                                            role="option"
                                                            aria-selected={highlightedIndex === idx}
                                                            className={`px-3 py-2 cursor-pointer rounded-md transition-all text-gray-700 text-sm flex items-start gap-2 ${highlightedIndex === idx ? 'bg-gray-100 text-[#31C65B]' : 'hover:bg-gray-100 hover:text-[#31C65B]'} `}
                                                            onMouseEnter={() => { setHighlightedIndex(idx); setUserNavigated(true); }}
                                                            onClick={() => selectOptionAtIndex(idx)}
                                                        >
                                                            <span className="font-medium line-clamp-2">{renderHighlighted(post.title || 'Untitled Post')}</span>
                                                        </div>
                                                    );
                                                })}
                                            </>
                                        )}

                                        {/* Solutions section */}
                                        {liveSolutionResults.length > 0 && (
                                            <>
                                                <h3 className="text-xs uppercase text-gray-400 px-1 mt-2" aria-hidden="true">Solutions</h3>
                                                {liveSolutionResults.map((sol) => {
                                                    const idx = options.findIndex(o => o.type === 'solution' && o.value.id === sol.id);
                                                    return (
                                                        <div
                                                            key={sol.id}
                                                            id={`${listboxId}-option-${idx}`}
                                                            role="option"
                                                            aria-selected={highlightedIndex === idx}
                                                            className={`px-3 py-2 cursor-pointer rounded-md transition-all text-gray-700 text-sm flex items-start gap-2 ${highlightedIndex === idx ? 'bg-gray-100 text-[#31C65B]' : 'hover:bg-gray-100 hover:text-[#31C65B]'} `}
                                                            onMouseEnter={() => { setHighlightedIndex(idx); setUserNavigated(true); }}
                                                            onClick={() => selectOptionAtIndex(idx)}
                                                        >
                                                            <span className="font-medium line-clamp-2">{renderHighlighted(sol.title || 'Untitled Solution')}</span>
                                                            {sol.type && <span className="ml-2 text-xs text-gray-500">{sol.type}</span>}
                                                        </div>
                                                    );
                                                })}
                                            </>
                                        )}
                                        {liveResults.length === 0 && livePostResults.length === 0 && liveSolutionResults.length === 0 && (
                                            <div className="px-3 py-2 text-sm text-gray-500">No matches. Try a different query.</div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="py-2">
                                        {recentSearches.length > 0 && (
                                            <div className="flex items-center justify-between text-sm text-gray-500 mb-3" aria-hidden="true">
                                                <span className="flex items-center gap-2">
                                                    <IconHistory size={16} />
                                                    <span>Recent Searches</span>
                                                </span>
                                                <button
                                                    type="button"
                                                    className="text-xs text-gray-400 hover:text-red-500"
                                                    onClick={clearRecent}
                                                >
                                                    Clear
                                                </button>
                                            </div>
                                        )}
                                        <div role="listbox" id={listboxId}>
                                            {recentSearches.map((term, idx) => (
                                                <div
                                                    key={`recent-${idx}`}
                                                    id={`${listboxId}-option-${idx}`}
                                                    role="option"
                                                    aria-selected={highlightedIndex === idx}
                                                    className={`px-3 py-2 cursor-pointer rounded-md transition-all text-gray-700 text-sm flex items-center gap-2 ${highlightedIndex === idx ? 'bg-gray-100 text-[#31C65B]' : 'hover:bg-gray-100 hover:text-[#31C65B]'} focus:outline-[#31C65B] focus:bg-gray-100`}
                                                    onMouseEnter={() => { setHighlightedIndex(idx); setUserNavigated(true); }}
                                                    onClick={() => {
                                                        const t = (term || '').trim();
                                                        if (!t) return;
                                                        addRecentTerm(t);
                                                        navigate(`/search?q=${encodeURIComponent(t)}`);
                                                        setShowDropdown(false);
                                                        dispatch(setSearchQuery(''));
                                                    }}
                                                >
                                                    {term}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

// Child component to render a single live result using centralized users cache
const LiveResultItem = ({ user, renderHighlighted }) => {
    // Read from cache only to avoid duplicate fetches; parent already batches with fetchUsersByIds
    const cached = useSelector((s) => selectUserById(s, user.id));
    const currentUserUid = useSelector((s) => s.auth.currentUser?.uid); // Get current user UID

    const display = useMemo(() => {
        const name = getDisplayName({
            profile: (cached || user) || null,
            candidates: [
                cached?.username,
                cached?.displayName,
                cached?.name,
                user?.username,
                user?.displayName,
                user?.name
            ]
        });
        const profession = cached?.profession || cached?.userProfession || user.userProfession || '';
        // Use centralized photo URL resolver for consistency
        const photoURL = getResolvedPhotoURL({
            profile: cached,
            user: user,
            authUser: user?.id === currentUserUid ? { photoURL: cached?.photoURL || user?.photoURL } : null,
            candidates: []
        });
        return { name, profession, photoURL };
    }, [cached, user, currentUserUid]);

    return (
        <>
            <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden bg-gray-200">
                {display.photoURL ? (
                    <img
                        src={display.photoURL}
                        alt={`${display.name}'s avatar`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                    />
                ) : (
                    <IconUserCircle size={32} />
                )}
            </div>
            <div className="flex flex-col ml-3">
                <h4 className="font-medium text-sm">{renderHighlighted ? renderHighlighted(display.name) : display.name}</h4>
                {display.profession && (
                    <p className="flex items-center text-xs text-gray-500 mt-1">
                        <IconBriefcase size={14} />
                        <span>{renderHighlighted ? renderHighlighted(display.profession) : display.profession}</span>
                    </p>
                )}
            </div>
        </>
    );
};

export default SearchComponent;